import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import LocalStorageService from './localStorageService'
import { HttpStatusCode } from '../const/HttpStatusCode'
import { API_BASE_URL, AUTH_ENDPOINTS } from '../const/EndPoints'
import { User } from '../models/User'
import { generateUUID } from '../utils/uuid'
import { getBrowserLocation } from '../utils/geolocation'

// Extend InternalAxiosRequestConfig to include contentType
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  contentType?: 'application/json' | 'multipart/form-data'
  _retry?: boolean
  _idempotencyKey?: string
}

// Create Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
})

// Define the response type for the token refresh
interface RefreshTokenResponse {
  accessToken: string
}

// Shared refresh state — ensures only one refresh runs at a time
let refreshPromise: Promise<string> | null = null

// Function to refresh the access token
const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const user = LocalStorageService.getItem<User>('user')
      if (!user || !user.userId) {
        throw new Error('User not available')
      }

      const sessionId = LocalStorageService.getItem<string>('sessionId')

      const response: AxiosResponse<RefreshTokenResponse> = await api.post(
        AUTH_ENDPOINTS.TOKEN,
        {
          userId: user.userId,
          ...(sessionId && { sessionId }),
        }
      )
      const { accessToken } = response.data

      // Save new access token
      LocalStorageService.setItem('accessToken', accessToken)

      return accessToken
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// Compute SHA-256 body digest for mutation requests
const computeBodyDigest = async (data: unknown): Promise<string> => {
  const body =
    typeof data === 'string' ? data : JSON.stringify(data ?? '')
  const encoded = new TextEncoder().encode(body)
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded)
  const hashBase64 = btoa(
    String.fromCharCode(...new Uint8Array(hashBuffer))
  )
  return `SHA-256=${hashBase64}`
}

// Request Interceptor: Add Authorization header, timestamp, and body digest
api.interceptors.request.use(
  async (
    config: CustomAxiosRequestConfig
  ): Promise<CustomAxiosRequestConfig> => {
    const accessToken = LocalStorageService.getItem('accessToken')
    config.headers = config.headers || {}

    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Attach fresh browser geolocation on auth requests (login, OAuth, token refresh)
    const url = config.url || ''
    const isAuthRequest =
      url.includes('/auth/login') ||
      url.includes('/auth/social/signin') ||
      url.includes('/auth/token')
    if (isAuthRequest) {
      const location = await getBrowserLocation()
      if (location) {
        config.headers['X-User-Location'] = location
      }
    }

    // Request timestamp for replay attack prevention
    config.headers['X-Request-Timestamp'] = Math.floor(
      Date.now() / 1000
    ).toString()

    // Mutation request headers (POST, PUT, PATCH, DELETE)
    const method = (config.method || '').toUpperCase()
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      // Reuse idempotency key on retries, generate fresh for new requests
      config.headers['X-Idempotency-Key'] =
        config._idempotencyKey || generateUUID()
      config._idempotencyKey = config.headers['X-Idempotency-Key']

      // Body digest for integrity verification
      // Skip FormData — multipart boundaries are non-deterministic
      if (config.data && !(config.data instanceof FormData)) {
        config.headers['X-Body-Digest'] = await computeBodyDigest(config.data)
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response Interceptor: Refresh the token on 401 error and retry the request
api.interceptors.response.use(
  (response) => response.data, // Pass through successful responses
  async (error) => {
    if (error.code === 'ERR_NETWORK') {
      return Promise.reject(
        new Error('Couldn\'t reach Treatwise — please check your connection and try again.')
      )
    }

    const originalRequest = error.config as CustomAxiosRequestConfig

    if (
      error.response &&
      error.response.status === HttpStatusCode.UNAUTHORIZED &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true
      try {
        const newAccessToken = await refreshAccessToken()
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
        return api(originalRequest) // Retry the original request with new token
      } catch (err) {
        LocalStorageService.clear()
        window.location.href = '/'
        return Promise.reject(err)
      }
    }

    // Unwrap standardised ErrorEnvelope: { error: { code, message, details } }
    // Callers receive a flat object: { status, message, details, ... }
    const body = error?.response?.data
    const envelope = body?.error
    if (envelope && typeof envelope === 'object') {
      return Promise.reject({
        status: envelope.code || error?.response?.status || 0,
        message: envelope.message,
        details: envelope.details,
      })
    }

    // Legacy / non-envelope errors — preserve HTTP status
    const rejection = body || error
    if (error?.response?.status && typeof rejection === 'object' && rejection !== null) {
      rejection.status = error.response.status
    }
    return Promise.reject(rejection)
  }
)

export default api
